import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Tag } from "@pack-it-up/ui-web";
import { MAX_TAGS_PER_ROW, MAX_TAG_LENGTH, normalizeTags, tagKey } from "@pack-it-up/core";
import * as styles from "./TagField.css.js";

// The composer's Tags field (T29 / issue #99). The palette is derived from the list's
// own rows, so tagging an item is one click on an existing chip; "+ New tag" opens an
// inline input for a tag nobody has used yet. Every change is normalized in core, so
// casing/whitespace variants collapse to a single tag.

export interface TagFieldProps {
  value: string[];
  /** Tags already in use anywhere on this trip/template. */
  palette: string[];
  onChange: (tags: string[]) => void;
}

export const TagField = ({ value, palette, onChange }: TagFieldProps): ReactNode => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const selectedKeys = new Set(value.map(tagKey));
  const atCap = value.length >= MAX_TAGS_PER_ROW;

  const toggle = (tag: string): void => {
    const next = selectedKeys.has(tagKey(tag))
      ? value.filter((t) => tagKey(t) !== tagKey(tag))
      : [...value, tag];
    onChange(normalizeTags(next));
  };

  const commitDraft = (): void => {
    const next = normalizeTags([...value, draft]);
    if (next.length > 0) onChange(next);
    setDraft("");
    setAdding(false);
  };

  // The composer commits the whole row on Enter and cancels on Esc (see FormShell), so
  // both keys have to stop here while the user is typing a tag name.
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      commitDraft();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setDraft("");
      setAdding(false);
    }
  };

  // Selected tags first (they may not be in the palette yet if just typed), then the
  // rest of the palette.
  const unselected = palette.filter((t) => !selectedKeys.has(tagKey(t)));

  return (
    <div className={styles.wrap} role="group" aria-label="Tags">
      {value.map((tag) => (
        <Tag key={tag} label={tag} selected onRemove={() => toggle(tag)} />
      ))}
      {!atCap &&
        unselected.map((tag) => <Tag key={tag} label={tag} onClick={() => toggle(tag)} />)}
      {atCap && <span className={styles.hint}>{MAX_TAGS_PER_ROW} tag maximum</span>}
      {!atCap &&
        (adding ? (
          <input
            aria-label="New tag"
            className={styles.input}
            maxLength={MAX_TAG_LENGTH}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commitDraft}
          />
        ) : (
          <button type="button" className={styles.addButton} onClick={() => setAdding(true)}>
            + New tag
          </button>
        ))}
    </div>
  );
};
